/*
ArrKey — port direct de `app/src/main/java/app/arbre/data/ArrKey.kt` côté
Android. Clé géographique dérivée du suffixe d'adresse (« ..., PARIS 5E
ARRDT » / « ..., BOIS DE VINCENNES »). Sert de dénominateur aux badges
« Familier d'arrondissement » et de pivot pour les barres Profile
« Arrondissements visités / complétés » (mirror Android v1.2.0).

Représentation : union discriminée. `kind: 'other'` couvre exclaves,
Hauts-de-Seine, et adresses sans suffixe reconnaissable.
*/

export type ArrKey =
	| { kind: 'paris'; num: number }
	| { kind: 'vincennes' }
	| { kind: 'boulogne' }
	| { kind: 'other' }

const kParisRaw = /^PARIS (\d{1,2})(?:ER|E) ARRDT$/  // format brut OpenData (« PARIS 12E ARRDT »)
const kParisNormalized = /^(\d{1,2})(?:er|e)$/  // format normalisé toléré (« 5e », « 1er »)

/*
Parse l'arrondissement depuis le segment post-dernière-virgule. Tolère
les deux formats. Retourne `{kind:'other'}` pour les adresses null/vides
et celles sans suffixe reconnaissable.
*/
export function parseArrKey(inAdresse: string | null | undefined): ArrKey
{
	if (!inAdresse) return { kind: 'other' }
	const vTrim = inAdresse.trim()
	if (vTrim.length === 0) return { kind: 'other' }
	const vIdx = vTrim.lastIndexOf(', ')
	const vTail = (vIdx === -1 ? vTrim : vTrim.slice(vIdx + 2)).trim()

	const vMatch = kParisRaw.exec(vTail) ?? kParisNormalized.exec(vTail)
	if (vMatch)
	{
		const vN = parseInt(vMatch[1]!, 10)
		if (Number.isFinite(vN) && vN >= 1 && vN <= 20) return { kind: 'paris', num: vN }
	}

	const vUp = vTail.toUpperCase()
	if (vUp === 'BOIS DE VINCENNES') return { kind: 'vincennes' }
	if (vUp === 'BOIS DE BOULOGNE') return { kind: 'boulogne' }
	return { kind: 'other' }
}

/*
Libellé court : « 1er », « 5e », « Bois de Vincennes »… Utilisé pour le
sticky header de l'Arboretum (futur) et les libellés de badges
« Familier du ... ».
*/
export function arrKeyLabel(inKey: ArrKey): string
{
	switch (inKey.kind)
	{
	case 'paris': return inKey.num === 1 ? '1er' : `${inKey.num}e`
	case 'vincennes': return 'Bois de Vincennes'
	case 'boulogne': return 'Bois de Boulogne'
	case 'other': return 'Hors Paris'
	}
}

/*
Ordre canonique : 1..20 → Vincennes → Boulogne → Other. Sert au tri
des clés dans `arrSpecies.ts` et au rendu Profile (barre 6/7).
*/
export function arrKeySortKey(inKey: ArrKey): number
{
	switch (inKey.kind)
	{
	case 'paris': return inKey.num
	case 'vincennes': return 21
	case 'boulogne': return 22
	case 'other': return 23
	}
}

/*
Slug stable utilisé comme clé dans `arr-species.json` et comme suffixe
d'id de badge « Familier du ... » (`familier_arr_{slug}`). Doit rester
aligné avec `arr_key_slug()` côté `tools/build_dataset.py` Android.
*/
export function arrKeyIdSlug(inKey: ArrKey): string
{
	switch (inKey.kind)
	{
	case 'paris': return String(inKey.num)
	case 'vincennes': return 'vincennes'
	case 'boulogne': return 'boulogne'
	case 'other': return 'other'
	}
}

/*
Inverse de `arrKeyIdSlug`. Reconstruit l'ArrKey depuis un slug stocké
dans `arr-species.json` ou dans un id de badge. Retourne null pour les
slugs inconnus.
*/
export function arrKeyFromSlug(inSlug: string): ArrKey | null
{
	if (inSlug === 'vincennes') return { kind: 'vincennes' }
	if (inSlug === 'boulogne') return { kind: 'boulogne' }
	if (inSlug === 'other') return { kind: 'other' }
	const vN = parseInt(inSlug, 10)
	if (Number.isFinite(vN) && vN >= 1 && vN <= 20 && String(vN) === inSlug) return { kind: 'paris', num: vN }
	return null
}

/*
Égalité structurelle. Les ArrKey ne sont pas garanties uniques par
identité (parse retourne de nouveaux objets), donc Set/Map doivent
indexer sur `arrKeyIdSlug` plutôt que sur l'objet directement.
*/
export function arrKeyEquals(inA: ArrKey, inB: ArrKey): boolean
{
	if (inA.kind !== inB.kind) return false
	if (inA.kind === 'paris' && inB.kind === 'paris') return inA.num === inB.num
	return true
}

/*
Libellé long « 1er arrondissement » / « 5e arrondissement ». Utilisé sur
les sticky / chapter headers de Catalogue Remarquables + Arboretum.
Bois et « Hors Paris » inchangés. Mirror Android v1.4.0
ArrKey.headerLabel.
*/
export function arrKeyHeaderLabel(inKey: ArrKey): string
{
	switch (inKey.kind)
	{
	case 'paris': return inKey.num === 1 ? '1er arrondissement' : `${inKey.num}e arrondissement`
	case 'vincennes': return 'Bois de Vincennes'
	case 'boulogne': return 'Bois de Boulogne'
	case 'other': return 'Hors Paris'
	}
}

/*
Normalisation des chaînes pour la recherche : NFD + strip diacritiques +
lowercase + trim. Utilisée par `searchData.ts` (haystack pré-cuit) et par
`parseArrQuery` ci-dessous. Mirror Android v1.4.0 SearchData.normalizeQuery.
*/
export function normalizeQuery(inQuery: string): string
{
	return inQuery.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
}

const kArrOrdinalLitterals: Readonly<Record<string, number>> = Object.freeze({
	premier: 1, premiere: 1,
	deuxieme: 2, second: 2, seconde: 2,
	troisieme: 3,
	quatrieme: 4,
	cinquieme: 5,
	sixieme: 6,
	septieme: 7,
	huitieme: 8,
	neuvieme: 9,
	dixieme: 10,
	onzieme: 11,
	douzieme: 12,
	treizieme: 13,
	quatorzieme: 14,
	quinzieme: 15,
	seizieme: 16,
	dixseptieme: 17, 'dix-septieme': 17,
	dixhuitieme: 18, 'dix-huitieme': 18,
	dixneuvieme: 19, 'dix-neuvieme': 19,
	vingtieme: 20,
})

/*
Parse d'une query utilisateur en ArrKey si reconnaissable. Cascade :
  1. chiffres bruts ou avec suffixe ordinal (« 5 », « 5e », « 1er », « 4eme »)
  2. codes postaux (« 75005 », « 75020 »)
  3. ordinaux français littéraux (« cinquieme » / « premier » / « vingtieme »)
  4. mots-clés bois (« vincennes » / « bois de vincennes »…)
Retourne null si rien ne matche. Mirror Android v1.4.0 SearchData.parseArrQuery.
*/
export function parseArrQuery(inQuery: string): ArrKey | null
{
	const vQ = normalizeQuery(inQuery)
	if (vQ.length === 0) return null

	// 1. chiffres bruts + ordinal abrégé
	const vDigits = /^(\d{1,2})(?:er|e|eme|ieme)?$/.exec(vQ)
	if (vDigits)
	{
		const vN = parseInt(vDigits[1]!, 10)
		if (Number.isFinite(vN) && vN >= 1 && vN <= 20) return { kind: 'paris', num: vN }
	}

	// 2. codes postaux Paris (75001..75020)
	const vCp = /^750(\d{2})$/.exec(vQ)
	if (vCp)
	{
		const vN = parseInt(vCp[1]!, 10)
		if (Number.isFinite(vN) && vN >= 1 && vN <= 20) return { kind: 'paris', num: vN }
	}

	// 3. ordinaux français littéraux
	const vLit = kArrOrdinalLitterals[vQ]
	if (typeof vLit === 'number') return { kind: 'paris', num: vLit }

	// 4. mots-clés bois (tolère « bois de … »)
	if (vQ === 'vincennes' || vQ === 'bois de vincennes' || vQ === 'bois vincennes') return { kind: 'vincennes' }
	if (vQ === 'boulogne' || vQ === 'bois de boulogne' || vQ === 'bois boulogne') return { kind: 'boulogne' }

	return null
}
